import styles from "./ScheduleClasses.module.scss";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import useAppSelector from "../../../hooks/useAppSelector";
import Spinner from "../../../components/Spiner/Spinner";
import {ClassEventService} from "../../../services/classEvent.service";
import moment from "moment";
import useAppDispatch from "../../../hooks/useAppDispatch";
import {loaderUpdate, setClassEvents, setPublicClassEvents} from "../../../store/classEvents/classEventsSlice";
import {IClassEvent, IClassEventPublic} from "../../../models/ClassEvent";
import CustomButton from "../../../components/UI/CustomButton/CustomButton";
import {getProfileImageByString} from "../../../helpers/image.helper";
import emptyIcon from "../../../assets/images/icons/empty-classes.svg";
import {openLoginPopup} from "../../../store/notification/notificationSlice";
import ScheduleInstructorPopup from "../ScheduleInstructorInfoPopup/ScheduleInstructorInfoPopup";
import ScheduleConfirmBookPopup from "../ScheduleConfirmBookPopup/ScheduleConfirmBookPopup";

interface ScheduleClassesProps {
    currentDate: string;
    selectedTrainers: number[];
    selectedClasses: number[];
}

const ScheduleClasses: React.FC<ScheduleClassesProps> = ({currentDate, selectedTrainers, selectedClasses}) => {

    const dispatch = useAppDispatch();
    const {t} = useTranslation();

    const {activeGym} = useAppSelector(state => state.multiGymsSlice);
    const {isAuthorized} = useAppSelector(state => state.profileSlice);
    const {classEvents, publicClassEvents, isLoading} = useAppSelector(state => state.classEventSlice);

    const [reload, setReload] = useState(false);
    const [filteredClasses, setFilteredClasses] = useState<{
        public: IClassEventPublic[],
        usual: IClassEvent[]
    }>({public: publicClassEvents, usual: classEvents});
    const [showConfirmBookingPopup, setShowConfirmBookingPopup] = useState<{
        show: boolean,
        classDetails: any,
        myReservation: any,
        myDropIn: any
    }>({
        show: false,
        classDetails: null,
        myReservation: null,
        myDropIn: null
    });
    const [showInstructorDetailPopup, setShowInstructorDetailPopup] = useState<{
        visible: boolean,
        classEvent: IClassEvent | IClassEventPublic | null
    }>({visible: false, classEvent: null});

    useEffect(() => {
        if (isAuthorized) {
            getClassEventList();
        } else {
            getPublicClassEventList();
        }
        setReload(false);
    }, [isAuthorized, currentDate, reload, activeGym]);

    useEffect(() => {
        if (isAuthorized) {
            filterUsualClasses();
        } else {
            filterPublicClasses();
        }
    }, [selectedClasses, selectedTrainers, isAuthorized, isLoading, classEvents, publicClassEvents, activeGym]);

    const getClassEventList = async () => {
        try {
            dispatch(loaderUpdate(true));
            const response = await ClassEventService.getClassEventsByDate({
                start: moment(currentDate).startOf("day").toISOString(),
                end: moment(currentDate).endOf("day").toISOString()
            });
            dispatch(setClassEvents(response.data));
        } catch (e) {}
    }

    const getPublicClassEventList = async () => {
        try {
            dispatch(loaderUpdate(true));
            const response = await ClassEventService.getPublicClassEventsByDate({
                start: moment(currentDate).startOf("day").toDate(),
                end: moment(currentDate).endOf("day").toDate()
            });
            dispatch(setPublicClassEvents(response.data));
        } catch (e) {
        }
    }

    const filterUsualClasses = () => {
        const filterByClassesUsual = classEvents.filter(el => {
            if (!selectedClasses.length) return true;
            return selectedClasses.includes(el.trainningCourseId)
        });
        const filteredByInstructorsUsual = filterByClassesUsual.filter(el => {
            if (!selectedTrainers.length) return true;
            return selectedTrainers.includes(el.instructorId);
        });
        setFilteredClasses({usual: filteredByInstructorsUsual, public: []});
    }

    const filterPublicClasses = () => {
        const filterByClassesUsual = publicClassEvents.filter(el => {
            if (!selectedClasses.length) return true;
            return selectedClasses.includes(el.trainningCourseId)
        });
        const filteredByInstructorsUsual = filterByClassesUsual.filter(el => {
            if (!selectedTrainers.length) return true;
            return selectedTrainers.includes(el.instructorId);
        });
        setFilteredClasses({usual: [], public: filteredByInstructorsUsual});
    }

    const clickOnBook = async (event: IClassEvent | null) => {
        if (!isAuthorized || !event) {
            dispatch(openLoginPopup({isOpen: true}));
            return;
        }

        const eventDetailById = await getEventDetailById(event.id);
        const registrations = await checkIAmRegistered(event.id);
        const eventEndDate = moment(eventDetailById.classDetails.occuranceDate).add(eventDetailById.classDetails.duration, "minutes");

        if (eventDetailById.classDetails.status !== 2 && moment(eventEndDate).isAfter(new Date())) {
            setShowConfirmBookingPopup({
                show: true,
                classDetails: eventDetailById.classDetails,
                myDropIn: registrations.myDropIn,
                myReservation: registrations.myReservation
            });
        }
    }

    const getEventDetailById = async (eventId: number) => {
        try {
            const response = await ClassEventService.getEventDetailById(eventId);
            return {
                classDetails: response.data.events
            }
        } catch (e) {
            return {
                classDetails: null
            }
        }
    }

    const checkIAmRegistered = async (eventId: number) => {
        try {
            const response = await ClassEventService.checkIAmRegistered(eventId);
            return {
                myReservation: response.data.reservation,
                myDropIn: response.data.dropIn
            }
        } catch (e) {
            return {
                myReservation: null,
                myDropIn: null
            }
        }
    }

    const closeConfirmBookingPopup = () => {
        setShowConfirmBookingPopup({show: false, classDetails: null, myDropIn: null, myReservation: null})
    }

    const clickOnInstructorImage = (classEvent: IClassEvent | IClassEventPublic) => {
        setShowInstructorDetailPopup({visible: true, classEvent});
    }

    const closeInstructorDetailPopup = () => {
        setShowInstructorDetailPopup({visible: false, classEvent: null});
    }

    const getClassCapacity = (classEvent: IClassEvent) => {
        const reservationLength = classEvent.reservations.length;
        const dropInReservationsLength = classEvent.dropinReservations.length;

        const totalLength = reservationLength + dropInReservationsLength;

        if (totalLength >= classEvent.capacity) return "Full";

        return `${reservationLength + dropInReservationsLength}/${classEvent.capacity}`;
    }

    const checkIsBookBtnDisabled = (classEvent: IClassEvent) => {
        if (getClassCapacity(classEvent) === "Full") {
            if (!classEvent.hasWaitingList) return true;
            const waitingListLength = classEvent.waitingList.length;
            if (waitingListLength >= classEvent.waitingListMax) return true;
        }
        return false;
    }

    const makeReload = () => {
        setReload(true);
    }

    if (isLoading) {
        return (
            <div className={styles.component}>
                <div className={styles.emptyWrapper}>
                    <Spinner/>
                </div>
            </div>
        )
    }

    if (isAuthorized) {
        return (
            <div className={styles.component}>
                {filteredClasses.usual.length ? (
                    <>
                        <ul className={styles.wrapper}>
                            {filteredClasses.usual.map((el, index) => {
                                return (
                                    <li key={el.id} className={`${styles.item} ${styles.authorized}`}>
                                        <div className={styles.classes}>
                                            <h4 className={styles.title}>{t("Class")}:</h4>
                                            <p className={styles.text}>{el.trainningCourseName}</p>
                                            <div className={styles.divider}></div>
                                            <p className={styles.text}>{el.duration}'</p>
                                            <div className={styles.divider}></div>
                                            <p className={styles.text}>{el.trainningRoomName}</p>
                                        </div>
                                        <div className={styles.timeAndTrainer}>
                                            <div className={styles.time}>
                                                <h4 className={styles.title}>{t("Start")}:</h4>
                                                <p className={styles.text}>{new Date(el.occuranceDate).getHours().toString().padStart(2, '0')}:{new Date(el.occuranceDate).getMinutes().toString().padStart(2, '0')}</p>
                                            </div>
                                            <div className={styles.trainer} onClick={() => clickOnInstructorImage(el)}>
                                                <h4 className={styles.title}>{t("Trainer")}:</h4>
                                                <p className={styles.text}>{el.instructorFirstName}</p>
                                                <div className={styles.profileImage}
                                                     style={{backgroundImage: `url(${getProfileImageByString(activeGym.endpoint, el.instructorProfileImage)})`}}></div>
                                            </div>
                                        </div>
                                        <div className={styles.capacity}>
                                            <h4 className={styles.title}>{t("ClassCapacity")}:</h4>
                                            <span className={styles.capacityLabel}>{getClassCapacity(el)}</span>
                                        </div>
                                        <div className={styles.btnWrapper}>
                                            <CustomButton
                                                text={t("Book")}
                                                type={"button"}
                                                typeStyle={"fill"}
                                                borderRadius={false}
                                                functionOnClick={() => clickOnBook(el)}
                                                disabled={checkIsBookBtnDisabled(el)}
                                            />
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                        {showInstructorDetailPopup.visible && showInstructorDetailPopup.classEvent && (
                            <ScheduleInstructorPopup classEvent={showInstructorDetailPopup.classEvent}
                                                     closePopup={closeInstructorDetailPopup}/>
                        )}
                        {showConfirmBookingPopup.show && showConfirmBookingPopup.classDetails && (
                            <ScheduleConfirmBookPopup
                                makeReload={makeReload}
                                closeConfirmBookingPopup={closeConfirmBookingPopup}
                                showConfirmBookingPopup={showConfirmBookingPopup}
                            />
                        )}
                    </>
                ) : (
                    <div className={styles.emptyWrapper}>
                        <img className={styles.emptyImage} src={emptyIcon} alt={"empty icon"}/>
                        <p className={styles.emptyText}>{t("NoClassesAvailable")}</p>
                    </div>
                )}
            </div>
        )
    } else {
        return (
            <div className={styles.component}>
                {filteredClasses.public.length ? (
                    <>
                        <ul className={styles.wrapper}>
                            {filteredClasses.public.map((el) => {
                                return (
                                    <li key={el.id} className={styles.item}>
                                        <div className={styles.classes}>
                                            <h4 className={styles.title}>{t("Class")}:</h4>
                                            <p className={styles.text}>{el.trainningCourseName}</p>
                                            <div className={styles.divider}></div>
                                            <p className={styles.text}>{el.duration}'</p>
                                            <div className={styles.divider}></div>
                                            <p className={styles.text}>{el.trainningRoomName}</p>
                                        </div>
                                        <div className={styles.timeAndTrainer}>
                                            <div className={styles.time}>
                                                <h4 className={styles.title}>{t("Start")}:</h4>
                                                <p className={styles.text}>{new Date(el.occuranceDate).getHours().toString().padStart(2, '0')}:{new Date(el.occuranceDate).getMinutes().toString().padStart(2, '0')}</p>
                                            </div>
                                            <div className={styles.trainer} onClick={() => clickOnInstructorImage(el)}>
                                                <h4 className={styles.title}>{t("Trainer")}:</h4>
                                                <p className={styles.text}>{el.instructorFirstName}</p>
                                                <div className={styles.profileImage}
                                                     style={{backgroundImage: `url(${getProfileImageByString(activeGym.endpoint, el.instructorProfileImage)})`}}></div>
                                            </div>
                                        </div>
                                        <div className={styles.btnWrapper}>
                                            <CustomButton
                                                text={t("Book")}
                                                type={"button"}
                                                typeStyle={"fill"}
                                                borderRadius={false}
                                                functionOnClick={() => clickOnBook(null)}
                                            />
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                        {showInstructorDetailPopup.visible && showInstructorDetailPopup.classEvent && (
                            <ScheduleInstructorPopup classEvent={showInstructorDetailPopup.classEvent}
                                                     closePopup={closeInstructorDetailPopup}/>
                        )}
                    </>

                ) : (
                    <div className={styles.emptyWrapper}>
                        <img className={styles.emptyImage} src={emptyIcon} alt={"empty icon"}/>
                        <p className={styles.emptyText}>{t("NoClassesAvailable")}</p>
                    </div>
                )}
            </div>
        )
    }
}

export default ScheduleClasses